/**
 * Turns the assistant's lightly-marked-up text into renderable segments so the
 * chat bubble never shows raw markup. The model is asked for plain text, but
 * occasionally emits Markdown; this is the defensive net:
 *   - emphasis markers (**bold**, *italic*, __bold__, `code`) are unwrapped,
 *   - "[text](url)" links become link segments the bubble renders as chips
 *     (styled like the suggestion chips) instead of bare URLs.
 */

export type Segment =
  | { type: 'text'; value: string }
  | { type: 'link'; text: string; href: string };

const LINK = /!?\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Drop paired emphasis markers; leave stray/odd symbols in ordinary text alone. */
function stripEmphasis(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/__([^_]+)__/g, '$1') // __bold__
    .replace(/\*([^*\n]+)\*/g, '$1') // *italic*
    .replace(/`([^`]+)`/g, '$1'); // `code`
}

/** Only http/https links become chips — anything else renders as plain text. */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

export function parseMessage(input: string): Segment[] {
  const segments: Segment[] = [];
  let cursor = 0;

  for (const match of input.matchAll(LINK)) {
    const at = match.index ?? 0;

    if (at > cursor) {
      segments.push({ type: 'text', value: stripEmphasis(input.slice(cursor, at)) });
    }

    const href = safeHref(match[2] ?? '');
    const text = stripEmphasis(match[1] ?? '');
    segments.push(href === null ? { type: 'text', value: text } : { type: 'link', text, href });

    cursor = at + match[0].length;
  }

  if (cursor < input.length) {
    segments.push({ type: 'text', value: stripEmphasis(input.slice(cursor)) });
  }

  return segments;
}
